import { z } from 'zod';
import { vec3Schema, viewIdSchema } from './input.js';
/* Manifest strings are rendered as text by consumers (A4 §4 rule 3); caps keep them bounded. */
const title = z.string().min(1).max(80);
const label = z.string().min(1).max(200);
const help = z.string().max(200);
export const poseSchema = z.looseObject({ position: vec3Schema, rotationY: z.number().optional() });
export const targetRefSchema = z.looseObject({
    id: z.string().min(1),
    kind: z.enum(['entity', 'control', 'tile']),
    bounds: z.looseObject({ x: z.number(), y: z.number(), w: z.number(), h: z.number() }).optional(),
});
export const hudModelSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean()]));
export const selectorSchema = z.looseObject({
    tag: z.string().optional(),
    prefab: z.string().optional(),
    ids: z.array(z.string()).optional(),
    where: z.record(z.string(), z.unknown()).optional(),
});
/** A4 §6 widget vocabulary plus B7 §3 `count.semantics`. */
export const widgetSchema = z.lazy(() => z.discriminatedUnion('kind', [
    z.looseObject({
        kind: z.literal('slider'),
        min: z.number(),
        max: z.number(),
        step: z.number().positive().optional(),
        unit: z.string().max(16).optional(),
    }),
    z.looseObject({ kind: z.literal('toggle') }),
    z.looseObject({
        kind: z.literal('choice'),
        options: z.array(z.looseObject({ value: z.string(), label, icon: z.string().max(200).optional() })).min(1),
    }),
    z.looseObject({ kind: z.literal('color'), palette: z.array(z.string()).optional() }),
    z.looseObject({
        kind: z.literal('count'),
        min: z.number().int(),
        max: z.number().int(),
        semantics: z.enum(['param', 'instances']),
    }),
    z.looseObject({ kind: z.literal('text'), maxLength: z.number().int().positive() }),
    z.looseObject({ kind: z.literal('place'), prefab: z.string().min(1) }),
    z.looseObject({ kind: z.literal('path') }),
    z.looseObject({ kind: z.literal('tilemap'), tileset: z.string().min(1) }),
    z.looseObject({
        kind: z.literal('rule'),
        template: z.string().max(200),
        slots: z.record(z.string(), widgetSchema),
    }),
    z.looseObject({ kind: z.literal('custom'), renderer: z.literal('game-overlay'), id: z.string().min(1) }),
]));
/** Stable forever, dotted lowercase: `enemy.color`. */
export const CONTROL_ID_PATTERN = /^[a-z][a-z0-9]*(\.[a-z][a-z0-9]*)+$/;
/** A4 §6 control, with B7 §3 scopes and without `aliases` (B7 §2 dropped NL synonyms). */
export const controlDeclSchema = z.looseObject({
    id: z.string().max(64).regex(CONTROL_ID_PATTERN),
    label,
    icon: z.string().max(200).optional(),
    help: help.optional(),
    group: z.string().max(200).optional(),
    scope: z.union([z.enum(['global', 'instance', 'prefab']), z.looseObject({ selector: selectorSchema })]),
    /** JSON pointer into the design doc, relative to each target. */
    path: z.string().regex(/^(\/([^~/]|~[01])*)+$/),
    widget: widgetSchema,
    default: z.unknown(),
    tier: z.enum(['basic', 'advanced']).optional(),
});
/** A4 §6 `core.manifest` fields plus the B7 §3 minimum additions. */
export const gameManifestSchema = z.looseObject({
    id: z.string().min(1).max(64),
    /** Semver range of the SDK the game targets. */
    sdk: z.string().min(1).max(64),
    title,
    inputs: z.looseObject({ actions: z.record(z.string().min(1).max(32), z.enum(['button', 'axis', 'stick'])) }),
    presentation: z.looseObject({
        orientation: z.enum(['landscape', 'portrait', 'any']),
        minLogical: z.looseObject({ w: z.number().positive(), h: z.number().positive() }),
        fit: z.enum(['responsive', 'fixed']),
    }),
    view: viewIdSchema,
    dimension: z.enum(['2d', '3d']),
    capabilities: z.array(z.string()),
    palette: z.looseObject({
        categories: z.array(z.looseObject({ id: z.string().min(1), label, kind: z.enum(['items', 'tools', 'panel']) })),
        items: z.array(z.looseObject({
            prefab: z.string().min(1),
            label,
            thumbnail: z.string().optional(),
            hint: help.optional(),
            category: z.string().min(1),
        })),
    }),
    tools: z.array(z.enum(['select', 'place', 'brush', 'sculpt', 'erase', 'path'])),
    gizmos: z.record(z.string(), z.array(z.enum(['translate', 'rotateYaw', 'elevate', 'splinePoints']))),
    grid: z.looseObject({ size: z.number().positive(), units: z.string().max(16) }).optional(),
    playLabel: z.string().min(1).max(80).optional(),
    saves: z.boolean(),
    overrides: z.looseObject({
        aliases: z.record(z.string(), z.string()),
        retired: z.array(z.string()),
        migrate: z.array(z.string()).optional(),
    }),
});
export const gameSchemas = {
    'game.v1': gameManifestSchema,
};
//# sourceMappingURL=game.js.map